<script setup lang="ts">
import { required, requiredIf } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { format } from 'date-fns'
import { capitalize, get } from 'lodash'
import { useRoute } from 'vue-router'
import { onMounted, ref, computed } from 'vue'
import { useExtendedI18n } from '@/i18n'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useModal } from '@/modules/shared/composables/use-modal'
import {
  ActionsMenu,
  ActionItem,
  ActionsGroup,
  VSection,
  VButton,
  VIcon,
  VModal,
  VTable,
  VTextField,
} from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { createOptions, markDisabledOptions } from '@/modules/shared/utils/form'
import { parse } from '@/modules/shared/utils/v-table'
import OtherEntityNav from '../components/other-entity-nav.vue'
import OtherEntityHeader from '../components/other-entity-header.vue'
import VSelect from '../components/v-select.vue'
import { useCommitmentStore } from '../stores/commitment-store'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { useOtherEntityStore } from '../stores/other-entity-store'
import { investorPath } from '../utils/investor'
import { addressColumns } from '../config/columns'

const { n, t } = useExtendedI18n()

///////////////////////////////////////////////////////////////////////////////
//
///////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////

const route = useRoute()
const skeleton = ref(true)
const authStore = useAuthStore()
const otherEntityStore = useOtherEntityStore()
const investorStore = useInvestingInvestorStore()
const commitmentStore = useCommitmentStore()
const investorFormModal = useModal()
const isAdmin = computed(() => authStore.is_site_or_group_admin)
const currentOtherEntity = ref(null)
const loading = ref(false)

const investorOptions = computed(() => {
  const disabledInvestors = [
    ...commitmentStore.commitments.map((c) => c.investor._cid),
    `funding-entity:${route.params.other_entity_id}`, // current entity can't invest in itself
  ]

  return markDisabledOptions(createOptions(investorStore.investorItems, { label: 'name' }), disabledInvestors)
})

const initialState = {
  id: null,
  date: format(new Date(), 'yyyy-MM-dd'),
  investor_id: null,
  ownership_percentage: null,
  amount: null,
}

const rules = {
  date: { required },
  investor_id: { required },
  ownership_override: { required: requiredIf(computed(() => !currentOtherEntity.value?.input_capital)) },
  amount: { required: requiredIf(computed(() => currentOtherEntity.value?.input_capital)) },
}
const commitment = ref({ ...initialState })
const v$ = useVuelidate(rules, commitment, { $lazy: true })

const commitmentError = ref<string | null>(null)

const openCommitmentForm = (item?) => {
  // reset
  v$.value.$reset()
  commitment.value = { ...initialState }
  commitmentError.value = null

  // assign item to commitment
  if (item) {
    commitment.value = {
      id: get(item, 'id'),
      investor_id: get(item, 'investor._cid'),
      date: get(item, 'date'),
      ownership_override: currentOtherEntity.value?.input_capital
        ? null
        : (parse(get(item, 'ownership_override') || 0, 'number') as number) * 100,
      amount: currentOtherEntity.value?.input_capital ? get(item, 'commitment.amount') : null,
      carried_interest_percentage: item.carried_interest_percentage
        ? (parse(item.carried_interest_percentage, 'number') as number) * 100
        : null,
      management_fee_percentage: item.management_fee_percentage
        ? (parse(item.management_fee_percentage, 'number') as number) * 100
        : null,
      preferred_return_percentage: item.preferred_return_percentage
        ? (parse(item.preferred_return_percentage, 'number') as number) * 100
        : null,
    }
  }

  investorFormModal.open()
}

const saveCommitment = async () => {
  commitmentError.value = null
  const valid = await v$.value.$validate()
  if (!valid) return

  const data = {
    date: commitment.value.date,
    investor_id: commitment.value.investor_id,
    capital: commitment.value.amount,
    ownership_override: currentOtherEntity.value?.input_capital ? null : commitment.value.ownership_override / 100,
  }

  loading.value = true
  if (commitment.value.id) {
    await commitmentStore.updateCommitment(commitment.value.id, data, 'funding-entity', route.params.other_entity_id)
    investorFormModal.close()
  } else {
    const error = await commitmentStore.addCommitment(data, 'funding-entity', route.params.other_entity_id)
    if (error) {
      commitmentError.value = error
    } else {
      investorFormModal.close()
    }
  }
  loading.value = false
}

const removeCommitment = async (item) => {
  if (!window.confirm('Are you sure?')) return
  await commitmentStore.removeCommitment(item.id, 'funding-entity', route.params.other_entity_id)
}

onMounted(async () => {
  await Promise.all([
    otherEntityStore.fetchOtherEntity(route.params.other_entity_id),
    commitmentStore.fetchCommitments('funding-entity', route.params.other_entity_id),
    investorStore.fetchInvestors(),
  ])
  currentOtherEntity.value = otherEntityStore.items.get(`funding-entity:${route.params.other_entity_id}`)
  skeleton.value = false
})
</script>

<template>
  <TheLayout>
    <OtherEntityHeader />
    <OtherEntityNav />
    <div class="-mt-7 mb-5 flex justify-end" v-if="isAdmin">
      <VButton :click="openCommitmentForm" size="md" variant="v-blue">
        <div class="mr-1 flex items-center space-x-2">
          <div><VIcon name="Plus" /></div>
          <div>{{ capitalize(t('shared.add investor', 0)) }}</div>
        </div>
      </VButton>
    </div>
    <VTable
      :columns="[
        {
          key: 'investor.name',
          name: capitalize(t('shared.investor')),
          sorted: true,
          type: 'string',
          fixed: true,
          is_visible: true,
        },
        {
          key: 'investor.first_name',
          name: capitalize(t('shared.first name')),
          type: 'string',
          align: 'left',
          is_visible: false,
        },
        {
          key: 'investor.last_name',
          name: capitalize(t('shared.last name')),
          type: 'string',
          align: 'left',
          is_visible: false,
        },
        {
          key: 'commitment',
          name: capitalize(t('shared.capital')),
          type: 'currency',
          aggregate: 'sum',
          is_visible: currentOtherEntity?.input_capital,
        },
        {
          key: 'ownership',
          name: capitalize(t('shared.ownership')),
          type: 'percent',
          is_visible: true,
        },
        {
          key: 'ownership_computed',
          name: capitalize(t('shared.ownership computed')),
          type: 'percent',
          is_visible: false,
          is_accessible: authStore.is_site_admin,
        },
        {
          key: 'ownership_override',
          name: capitalize(t('shared.ownership override')),
          type: 'percent',
          is_visible: false,
          is_accessible: authStore.is_site_admin,
        },
        {
          key: 'ownership_by_commitment',
          name: capitalize(t('shared.ownership by commitment')),
          type: 'percent',
          is_visible: false,
          is_accessible: authStore.is_site_admin,
        },
        {
          key: 'date',
          name: capitalize(t('shared.date')),
          type: 'date',
          is_visible: true,
        },
        {
          key: 'investor.email',
          name: capitalize(t('shared.email')),
          type: 'string',
          is_visible: false,
        },
        {
          key: 'investor.investing_name',
          name: capitalize(t('shared.legal name')),
          type: 'string',
          is_visible: false,
        },
        {
          key: 'investor.tax_id',
          name: `${t('shared.SSN')} / ${t('shared.EIN')}`,
          type: 'string',
          is_visible: false,
        },
        {
          key: 'investor.type',
          name: capitalize(t('shared.type')),
          type: 'string',
          is_visible: false,
        },
        {
          key: 'investor.phone',
          name: capitalize(t('shared.phone number')),
          type: 'string',
          is_visible: false,
        },
        ...addressColumns({ keyPrefix: 'investor' }),
        {
          key: 'actions',
          name: '',
          type: 'actions',
          is_visible: true,
        },
      ]"
      :items="commitmentStore.commitments"
      :name="`other-entity-${route.params.other_entity_id}-investors`"
      :skeleton="skeleton"
      :slots="[
        'actions',
        'investor.name',
        'ownership',
        'ownership_computed',
        'ownership_override',
        'ownership_by_commitment',
      ]"
    >
      <template #investor.name="{ item }">
        <RouterLink class="hyperlink" :to="investorPath(get(item, 'investor._cid'))">
          {{ get(item, 'investor.name') }}
        </RouterLink>
      </template>
      <template #ownership="{ item }">
        {{ n(get(item, 'ownership'), 'percent') }}
      </template>
      <template #ownership_computed="{ item }">
        {{ n(get(item, 'ownership_computed'), 'percent') }}
      </template>
      <template #ownership_override="{ item }">
        {{ n(get(item, 'ownership_override'), 'percent') }}
      </template>
      <template #ownership_by_commitment="{ item }">
        {{ n(get(item, 'ownership_by_commitment'), 'percent') }}
      </template>
      <template #actions="{ item }">
        <ActionsMenu v-if="isAdmin">
          <ActionsGroup :label="capitalize(t('shared.select action'))">
            <ActionItem
              icon="LinkExternal"
              :text="capitalize(t('shared.edit'))"
              @click="() => openCommitmentForm(item)"
              tag="a"
            />
            <ActionItem icon="Trash" :text="capitalize(t('shared.delete'))" @click="() => removeCommitment(item)" />
          </ActionsGroup>
        </ActionsMenu>
      </template>
    </VTable>

    <VModal :modalStore="investorFormModal">
      <template #main>
        <VSection :label="commitment.id ? 'Modify investor' : 'Add investor'">
          <div
            v-if="commitmentError"
            class="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            <VIcon name="ExclamationCircle" class="text-red-500" />
            <span>{{ commitmentError }}</span>
          </div>
          <form @submit.prevent="saveCommitment">
            <div class="mt-6 space-y-3">
              <VTextField
                v-model="commitment.date"
                :label="capitalize(t('shared.date'))"
                property="date"
                ref="autofocus"
                type="date"
                :v$="v$"
              />
              <VSelect
                v-model="commitment.investor_id"
                :disabled="commitment.id"
                :label="capitalize(t('shared.investor'))"
                :options="investorOptions"
                property="investor_id"
                :v$="v$"
              />
              <VTextField
                v-model="commitment.amount"
                :label="capitalize(t('shared.capital'))"
                :placeholder="n({ amount: '0', currency: currentOtherEntity?.currency, date: null }, 'currency')"
                property="amount"
                :v$="v$"
                v-if="currentOtherEntity?.input_capital"
              />
              <VTextField
                v-model="commitment.ownership_override"
                :label="capitalize(t('shared.ownership'))"
                :placeholder="n(0, 'percent')"
                property="ownership_override"
                :v$="v$"
                v-else
              />
            </div>
          </form>
        </VSection>
      </template>
      <template #footer>
        <div class="flex items-center justify-between space-x-3">
          <VButton :click="investorFormModal.close" size="lg">{{ capitalize(t('shared.close')) }}</VButton>
          <VButton :loading="loading" :click="saveCommitment" class="w-full" size="lg" variant="primary">
            <span v-if="commitment.id">{{ capitalize(t('shared.modify investor')) }}</span>
            <span v-else>{{ capitalize(t('shared.add investor')) }}</span>
          </VButton>
        </div>
      </template>
    </VModal>
  </TheLayout>
</template>
